import type { FieldNote } from '@/services/estimates-service'

// Opens a clean, clearly-labeled INTERNAL printout of an estimate's Project
// Notes and triggers the browser print dialog. This is a standalone browser
// print — it never touches the proposal PDF pipeline or the send-to-client
// flow, so the notes can never reach the customer's copy.

function esc(s: string): string {
  return s.replace(/[&<>"]/g, c => (({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string))
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

export function printProjectNotes(opts: { projectName?: string; notes: FieldNote[] }): void {
  // Only text notes — photo notes live in Assets, not on this sheet.
  const notes = opts.notes.filter(n => !n.photoAssetId)

  const rows = notes.length
    ? notes.map(n => `
        <div class="note">
          <div class="meta"><span class="author">${esc(n.authorName || 'Staff')}${n.inherited ? '<span class="from">From the original bid</span>' : ''}</span><span class="date">${fmt(n.createdAt)}</span></div>
          <div class="text">${esc(n.text)}</div>
        </div>`).join('')
    : `<div class="empty">No project notes yet.</div>`

  const win = window.open('', '_blank', 'width=820,height=920')
  if (!win) {
    alert('Please allow pop-ups for this site to print the notes.')
    return
  }

  const heading = opts.projectName ? esc(opts.projectName) : 'Project Notes'
  const printedAt = fmt(new Date().toISOString())

  win.document.write(`<!doctype html><html><head><meta charset="utf-8">
    <title>Project Notes${opts.projectName ? ' — ' + esc(opts.projectName) : ''}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;color:#1e293b;margin:36px}
      .banner{display:flex;gap:8px;align-items:baseline;background:#FEF3C7;border:1px solid #F6C544;border-left:4px solid #D9A404;border-radius:6px;padding:10px 14px;font-size:13px;color:#78350F;margin-bottom:22px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .banner strong{color:#92400E;text-transform:uppercase;letter-spacing:.03em;font-size:12px}
      h1{font-size:20px;margin:0 0 4px}
      .sub{color:#64748b;font-size:12.5px;margin-bottom:22px}
      .note{border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;margin-bottom:12px;page-break-inside:avoid}
      .meta{display:flex;justify-content:space-between;gap:12px;font-size:12px;color:#64748b;margin-bottom:6px}
      .author{font-weight:600;color:#334155}
      .from{font-weight:500;color:#64748b;font-size:11px;border:1px solid #cbd5e1;border-radius:999px;padding:1px 7px;margin-left:8px}
      .text{font-size:14px;line-height:1.5;white-space:pre-wrap}
      .empty{color:#94a3b8;font-style:italic}
      @media print{body{margin:.6in}}
    </style></head><body>
      <div class="banner"><strong>Internal &middot; Staff only</strong><span>These project notes are for your team. Do not share with the customer &mdash; they never appear on the proposal.</span></div>
      <h1>${heading}</h1>
      <div class="sub">Project Notes &middot; Printed ${printedAt}</div>
      ${rows}
    </body></html>`)
  win.document.close()
  win.focus()
  // Let the new document lay out before printing.
  setTimeout(() => { try { win.print() } catch { /* user closed it */ } }, 300)
}
